"use client"

import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@bednbody/ui/lib/utils"
import { Spinner } from "@bednbody/ui/components/spinner"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-button border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:bg-transparent dark:hover:bg-input/30",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
        // The therapist SOS (0273). SOLID, unlike `destructive`'s 10% tint: an
        // emergency control must read as one at a glance, one-handed, in bad light.
        sos: "bg-destructive font-semibold text-destructive-foreground hover:bg-destructive/90 focus-visible:border-destructive/40 focus-visible:ring-destructive/30",
      },
      size: {
        default:
          "h-9 gap-1.5 px-3 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        xs: "h-6 gap-1 px-2.5 text-xs has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 px-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        // `lg` is THE mobile size: rules/frontend-standards.md §11 tells apps/app and
        // apps/partner to default every Button to it "for large touch targets". It was
        // h-10 (40px), under the 44px minimum the same rules enforce for map controls —
        // so following the rule produced buttons that broke it, on every mobile CTA.
        // h-13 (52px) is the bar the rider navigation gate already asserted.
        lg: "h-13 gap-1.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        // The rider's primary card actions (start, pause, open in Maps): 60px and
        // a size up in type — glove-friendly on a handlebar (Leon, 2026-10-22).
        xl: "h-15 gap-2 px-5 text-base font-semibold has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4 [&_svg:not([class*='size-'])]:size-5",
        // The therapist app's ONE big action per screen: 64px, wraps rather than
        // truncates (docs/specs/therapist-app-mockup-redesign.md, rule 2).
        jumbo:
          "h-auto min-h-16 w-full gap-2.5 px-5 py-2 text-base leading-tight font-semibold whitespace-normal [&_svg:not([class*='size-'])]:size-6",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        // Same defect, icon-only: 40px was under the minimum. Raised to 44 rather than
        // matched to lg's 52 — an icon chip that tall reads as oversized next to the
        // map controls, and 44 is the documented floor.
        "icon-lg": "size-11",
      },
      // 2026-10-30 (Leon, option C): the default corner is 22px
      // (`rounded-button`) — almost a pill, not the full capsule. `pill`
      // keeps the capsule where a screen asks for it.
      shape: {
        default: "",
        pill: "rounded-full",
        // Marketing site: a calmer, squarer corner than the app's capsule buttons.
        soft: "rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  shape = "default",
  loading = false,
  disabled,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /**
     * The action behind this button is running.
     *
     * ⚠ THE BUTTON BLOCKS ITSELF. Every screen used to hand-roll `busy` state
     * plus a Spinner, so whoever forgot left a control that could be tapped
     * again — 33 of them across the three apps on 2026-09-20, including
     * securing a Session and submitting Report an Issue. `loading` disables the
     * button, marks it `aria-busy` for screen readers, and shows the spinner in
     * place of the leading icon, so the label never moves.
     */
    loading?: boolean
  }) {
  return (
    <ButtonPrimitive
      data-slot="button"
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, size, shape, className }))}
      {...props}
    >
      {loading ? <Spinner aria-hidden /> : null}
      {children}
    </ButtonPrimitive>
  )
}

export { Button, buttonVariants }
